import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, CheckCircle, Sparkles, UserSearch, Settings, ChevronRight, Loader2, Brain, AlertTriangle, FileDown, RefreshCw, X, MoreHorizontal, Pencil } from 'lucide-react';
import api from '../../lib/api';
import { jobRouteParam, jobUrlSegment, toSlug } from '../../lib/slug';
import { Job, Invitation, CampaignBudget } from '../../types';
import Button from '../../components/ui/Button';
import Badge from '../../components/ui/Badge';
import { Card, CardContent } from '../../components/ui/Card';
import { Tabs, TabsContent } from '../../components/ui/Tabs';
import StepTabsList from '../../components/ui/StepTabsList';
import DataTable from '../../components/ui/DataTable';
import ManualKOLSelector from '../../components/ManualKOLSelector';
import BroadcastConfirmModal, { type BroadcastPreviewData } from '../../components/BroadcastConfirmModal';
import EditMatchCriteriaModal from '../../components/EditMatchCriteriaModal';
import ShortlistRanker from '../../components/ShortlistRanker';
import CampaignChat from '../../components/CampaignChat';
import JobOperationsTab from '../../components/JobOperationsTab';
import JobContractsTab from '../../components/JobContractsTab';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import { formatDate } from '../../lib/utils';
import { AIInsightsPanel } from '../../components/AIInsightsPanel';
import { AIWorkflowPanel } from '../../components/AIWorkflowPanel';
import { AIPricingPanel } from '../../components/AIPricingPanel';
import { AIKOLDiscoveryPanel } from '../../components/AIKOLDiscoveryPanel';
import { GoogleWorkspaceJobPanel } from '../../components/google/GoogleWorkspaceJobPanel';
import AgencyJobScriptsSummary from '../../components/agency/AgencyJobScriptsSummary';
import { MESSAGING_EVENTS } from '../../lib/messagingConstants';
import { BROADCAST_INVITE_BATCH_MAX } from '../../lib/broadcastInviteLimits';
import { getApiErrorMessage } from '../../lib/apiError';

/** Which entry point opened the broadcast confirm step. */
type BroadcastSource = 'auto' | 'manual';

function jobStatusPill(status: string) {
  const s = status.toUpperCase();
  if (s === 'COMPLETED') return 'status-pill status-pill-muted';
  if (s === 'OPEN') return 'status-pill status-pill-info';
  if (s === 'ACTIVE') return 'status-pill status-pill-pos';
  if (['PAUSED', 'DRAFT'].includes(s)) return 'status-pill status-pill-warn';
  if (s === 'ARCHIVED') return 'status-pill status-pill-muted';
  return 'status-pill status-pill-muted';
}

function inviteStatusPill(status: string) {
  const s = status.toUpperCase();
  if (s === 'ACCEPTED') return 'status-pill status-pill-pos';
  if (['PENDING', 'VIEWED', 'MODIFICATION_REQUESTED'].includes(s)) return 'status-pill status-pill-warn';
  if (['DECLINED', 'REJECTED', 'WITHDRAWN', 'CANCELLED'].includes(s)) return 'status-pill status-pill-danger';
  return 'status-pill status-pill-muted';
}

const AGENCY_JOB_TABS = new Set([
  'brief',
  'matching',
  'contracts',
  'broadcast',
  'shortlist',
  'scripts',
  'submissions',
  'budget',
  'discovery',
  'chat',
  'operations',
  'notification',
  'workflow',
  'insights',
]);

const CAMPAIGN_TOOL_TABS = [
  { value: 'workflow', label: 'Workflow' },
  { value: 'insights', label: 'AI Insights' },
  { value: 'budget', label: 'Budget' },
  { value: 'discovery', label: 'Discovery' },
  { value: 'chat', label: 'Chat' },
  { value: 'operations', label: 'Operations' },
  { value: 'notification', label: 'Notifications' },
  { value: 'contracts', label: 'Contracts' },
];

export default function JobDetailPage() {
  const { id: rawId } = useParams();
  const jobParam = jobRouteParam(rawId);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParamRaw = searchParams.get('tab');
  const tabParam = tabParamRaw === 'matching' ? 'broadcast' : tabParamRaw;
  const activeMainTab =
    tabParam && AGENCY_JOB_TABS.has(tabParam) ? tabParam : 'brief';
  const setActiveMainTab = (v: string) => {
    const nextTab = v === 'matching' ? 'broadcast' : v;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('created');
        next.delete('published');
        if (nextTab === 'brief') next.delete('tab');
        else next.set('tab', nextTab);
        return next;
      },
      { replace: true },
    );
  };
  const [job, setJob] = useState<Job | null>(null);
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [shortlistRefreshing, setShortlistRefreshing] = useState(false);
  const [broadcasting, setBroadcasting] = useState(false);
  const [broadcastConfirm, setBroadcastConfirm] = useState<{ kolIds: string[]; source: BroadcastSource } | null>(
    null,
  );
  const [broadcastPreview, setBroadcastPreview] = useState<BroadcastPreviewData | null>(null);
  const [broadcastPreviewLoading, setBroadcastPreviewLoading] = useState(false);
  const [broadcastPreviewError, setBroadcastPreviewError] = useState<string | null>(null);
  const broadcastPreviewRequestRef = useRef(0);
  const [matchedKOLs, setMatchedKOLs] = useState<any[]>([]);
  const [selectedKOLIds, setSelectedKOLIds] = useState<string[]>([]);
  const [loadingMatches, setLoadingMatches] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [selectionMode, setSelectionMode] = useState<'auto' | 'manual'>('auto');
  const [showManualSelector, setShowManualSelector] = useState(false);
  const [showBroadcastMore, setShowBroadcastMore] = useState(false);
  const [showEditCriteria, setShowEditCriteria] = useState(false);
  const [showMobileActions, setShowMobileActions] = useState(false);
  const [changingStatus, setChangingStatus] = useState(false);
  const [aiRanking, setAiRanking] = useState(false);
  const [aiEnhanced, setAiEnhanced] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);
  const [campaignBudget, setCampaignBudget] = useState<CampaignBudget | null>(null);
  const [budgetForm, setBudgetForm] = useState({ allocatedRM: 0, spentRM: 0, alertAtPct: 90 });
  const [savingBudget, setSavingBudget] = useState(false);
  const [complianceApproved, setComplianceApproved] = useState(false);
  const [requireComplianceGate, setRequireComplianceGate] = useState(false);
  const [savingComplianceGate, setSavingComplianceGate] = useState(false);
  const [requireContractGate, setRequireContractGate] = useState(false);
  const [savingContractGate, setSavingContractGate] = useState(false);

  type MsgCh = { inApp: boolean; whatsapp: boolean; email: boolean; sms: boolean };
  const defaultMsgCh = (): MsgCh => ({
    inApp: true,
    whatsapp: false,
    email: true,
    sms: false,
  });
  const mergeMsgCh = (partial?: Partial<MsgCh>): MsgCh => ({
    inApp: partial?.inApp !== false,
    email: partial?.email !== false,
    /** WhatsApp / SMS in UI are coming soon; always off when saving. */
    whatsapp: false,
    sms: false,
  });
  const [messagingEnabled, setMessagingEnabled] = useState(true);
  const [messagingEventFlags, setMessagingEventFlags] = useState<Record<string, MsgCh>>(() => {
    const o: Record<string, MsgCh> = {};
    for (const ev of MESSAGING_EVENTS) o[ev.key] = defaultMsgCh();
    return o;
  });
  const [savingMessaging, setSavingMessaging] = useState(false);
  type ExtCh = 'WHATSAPP' | 'EMAIL' | 'SMS';
  type TemplateRefsState = Record<string, Partial<Record<ExtCh, string>>>;
  const [messagingTemplateRefs, setMessagingTemplateRefs] = useState<TemplateRefsState>({});
  const [messagingVariants, setMessagingVariants] = useState<
    { id: string; name: string; eventKey: string; channel: string }[]
  >([]);

  // Script management state
  const [jobScripts, setJobScripts] = useState<any[]>([]);
  const [actionNotice, setActionNotice] = useState<{ type: 'success' | 'error' | 'info'; message: string } | null>(
    null,
  );

  const statusTransitions: Record<string, string[]> = {
    DRAFT: ['OPEN'],
    OPEN: ['ACTIVE', 'PAUSED', 'ARCHIVED'],
    ACTIVE: ['PAUSED', 'COMPLETED'],
    PAUSED: ['ACTIVE', 'ARCHIVED'],
    COMPLETED: ['ARCHIVED'],
    ARCHIVED: [],
  };

  const handleStatusChange = async (newStatus: string) => {
    if (!job) return;
    try {
      setChangingStatus(true);
      await api.put(`/jobs/${encodeURIComponent(job.id)}/status`, { status: newStatus });
      await loadJob();
      if (newStatus === 'OPEN') {
        setActiveMainTab('broadcast');
      }
    } catch (error: any) {
      console.error('Failed to change status:', error);
      setActionNotice({
        type: 'error',
        message: error.response?.data?.error || 'Failed to change status',
      });
    } finally {
      setChangingStatus(false);
    }
  };

  useEffect(() => {
    loadJob();
  }, [jobParam]);

  useEffect(() => {
    if (!job?.id) return;
    setSelectionMode((job.jobTags?.length ?? 0) > 0 ? 'auto' : 'manual');
  }, [job?.id, job?.jobTags?.length]);

  const loadScripts = async (jid: string) => {
    try {
      const res = await api.get('/scripts', { params: { jobId: jid, omitContent: 'true' } });
      setJobScripts(res.data);
    } catch (e) {
      console.error('Failed to load scripts:', e);
    }
  };

  const refreshShortlist = async () => {
    if (!jobParam) return;
    setShortlistRefreshing(true);
    try {
      await loadJob();
    } finally {
      setShortlistRefreshing(false);
    }
  };

  const loadJob = async () => {
    if (!jobParam) return;
    try {
      const jobRes = await api.get(`/jobs/${encodeURIComponent(jobParam)}`);
      const jid = jobRes.data.id as string;
      setJob(jobRes.data);
      setRequireComplianceGate(!!jobRes.data.requireComplianceBeforeBroadcast);
      setRequireContractGate(!!jobRes.data.requireContractBeforeBroadcast);
      const ms = jobRes.data.messagingSettings;
      setMessagingEnabled(ms?.enabled !== false);
      const nextFlags: Record<string, MsgCh> = {};
      for (const ev of MESSAGING_EVENTS) {
        const raw = ms?.events?.[ev.key];
        nextFlags[ev.key] = mergeMsgCh(raw);
      }
      setMessagingEventFlags(nextFlags);
      const tr = (ms?.templateRefs || {}) as TemplateRefsState;
      setMessagingTemplateRefs(tr);

      const [invitationsRes, budgetRes, ccRes] = await Promise.all([
        api.get(`/invitations?jobId=${encodeURIComponent(jid)}`),
        api.get(`/jobs/${encodeURIComponent(jid)}/campaign-budget`).catch(() => ({ data: null })),
        api.get(`/jobs/${encodeURIComponent(jid)}/compliance-checklist`).catch(() => ({ data: null })),
      ]);
      setInvitations(invitationsRes.data);
      setComplianceApproved(!!ccRes.data?.approved);
      const b = budgetRes.data;
      if (b) {
        setCampaignBudget(b);
        setBudgetForm({
          allocatedRM: b.allocatedRM,
          spentRM: b.spentRM,
          alertAtPct: b.alertAtPct,
        });
      }
      void loadScripts(jid);
      if (jobRes.data.managedByOrgId) {
        try {
          const vRes = await api.get<{
            variants: { id: string; name: string; eventKey: string; channel: string }[];
          }>('/messaging-templates', {
            params: {
              organizationId: jobRes.data.managedByOrgId,
              includeVariants: '1',
            },
          });
          setMessagingVariants(vRes.data.variants || []);
        } catch {
          setMessagingVariants([]);
        }
      } else {
        setMessagingVariants([]);
      }
    } catch (error) {
      console.error('Failed to load job:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveBudget = async () => {
    const jid = job?.id ?? jobParam;
    if (!jid) return;
    try {
      setSavingBudget(true);
      const res = await api.put(`/jobs/${encodeURIComponent(jid)}/campaign-budget`, budgetForm);
      setCampaignBudget(res.data);
    } catch (e: any) {
      alert(e.response?.data?.error || 'Failed to save budget');
    } finally {
      setSavingBudget(false);
    }
  };

  const downloadCampaignPdf = async () => {
    if (!job) return;
    try {
      const res = await api.get(`/jobs/${encodeURIComponent(job.id)}/campaign-summary.pdf`, {
        responseType: 'blob',
        params: { brandTitle: job.title },
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `campaign-${job.title.slice(0, 24).replace(/\s+/g, '-')}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
      alert('Could not download PDF');
    }
  };

  const loadMatchingKOLs = async () => {
    const jid = job?.id ?? jobParam;
    try {
      setLoadingMatches(true);
      setHasSearched(true);
      const response = await api.get(`/jobs/${encodeURIComponent(jid)}/preview-matches`);
      const kols = response.data.kols || [];
      setMatchedKOLs(kols);
      setSelectedKOLIds(kols.slice(0, BROADCAST_INVITE_BATCH_MAX).map((kol: { id: string }) => kol.id));
    } catch (error: any) {
      setActionNotice({
        type: 'error',
        message: error.response?.data?.error || 'Failed to load matching KOLs',
      });
    } finally {
      setLoadingMatches(false);
    }
  };

  const loadAIEnhancedMatches = async () => {
    const jid = job?.id ?? jobParam;
    try {
      setAiRanking(true);
      setAiError(null);
      const response = await api.post('/ai/broadcast/rerank', {
        jobId: jid,
        maxCandidates: 30,
      });
      setMatchedKOLs(response.data.kols || []);
      setSelectedKOLIds(response.data.kols.map((kol: any) => kol.id));
      setAiEnhanced(response.data.aiAvailable ?? false);
      if (!response.data.aiAvailable && response.data.fallbackReason) {
        setAiError(`AI unavailable: ${response.data.fallbackReason}. Showing deterministic scores only.`);
      }
    } catch (error: any) {
      setAiError(error.response?.data?.error || 'AI ranking failed');
    } finally {
      setAiRanking(false);
    }
  };

  const saveComplianceGateSetting = async (checked: boolean) => {
    const jid = job?.id ?? jobParam;
    if (!jid) return;
    try {
      setSavingComplianceGate(true);
      await api.patch(`/jobs/${encodeURIComponent(jid)}`, { requireComplianceBeforeBroadcast: checked });
      setRequireComplianceGate(checked);
      await loadJob();
    } catch (e: any) {
      alert(e.response?.data?.error || 'Failed to save');
    } finally {
      setSavingComplianceGate(false);
    }
  };

  const saveContractGateSetting = async (checked: boolean) => {
    const jid = job?.id ?? jobParam;
    if (!jid) return;
    try {
      setSavingContractGate(true);
      await api.patch(`/jobs/${encodeURIComponent(jid)}`, { requireContractBeforeBroadcast: checked });
      setRequireContractGate(checked);
      await loadJob();
    } catch (e: any) {
      alert(e.response?.data?.error || 'Failed to save');
    } finally {
      setSavingContractGate(false);
    }
  };

  const normalizeMessagingEventsForSave = useCallback(
    (flags: Record<string, MsgCh>): Record<string, MsgCh> => {
      const out: Record<string, MsgCh> = {};
      for (const ev of MESSAGING_EVENTS) {
        const f = flags[ev.key] || defaultMsgCh();
        out[ev.key] = {
          ...f,
          whatsapp: false,
          sms: false,
        };
      }
      return out;
    },
    []
  );

  const saveMessagingSettings = async () => {
    const jid = job?.id ?? jobParam;
    if (!jid) return;
    try {
      setSavingMessaging(true);
      await api.patch(`/jobs/${encodeURIComponent(jid)}`, {
        messagingSettings: {
          enabled: messagingEnabled,
          events: normalizeMessagingEventsForSave(messagingEventFlags),
          templateRefs: messagingTemplateRefs,
        },
      });
      await loadJob();
    } catch (e: any) {
      alert(e.response?.data?.error || 'Failed to save notification settings');
    } finally {
      setSavingMessaging(false);
    }
  };

  const closeBroadcastConfirm = () => {
    // Bump the request id so a preview still in flight cannot repopulate the modal.
    broadcastPreviewRequestRef.current += 1;
    setBroadcastConfirm(null);
    setBroadcastPreview(null);
    setBroadcastPreviewError(null);
    setBroadcastPreviewLoading(false);
  };

  /**
   * Opens the confirm step and fetches the rendered sample email. The preview is
   * read-only and sends nothing; if it fails we still show the count and let the
   * send proceed — a preview must never block a broadcast.
   */
  const openBroadcastConfirm = (kolIds: string[], source: BroadcastSource) => {
    const requestId = broadcastPreviewRequestRef.current + 1;
    broadcastPreviewRequestRef.current = requestId;

    setBroadcastConfirm({ kolIds, source });
    setBroadcastPreview({
      count: kolIds.length,
      // Names we already know locally, so the modal is useful before the fetch lands.
      kolNames: matchedKOLs
        .filter((kol) => kolIds.includes(kol?.id))
        .map((kol) => String(kol?.name ?? '').trim())
        .filter(Boolean),
      email: null,
    });
    setBroadcastPreviewError(null);
    setBroadcastPreviewLoading(true);

    const jid = job?.id ?? jobParam;
    void (async () => {
      try {
        const res = await api.post(`/jobs/${encodeURIComponent(jid)}/broadcast/preview`, { kolIds });
        if (broadcastPreviewRequestRef.current !== requestId) return;
        const data = (res.data ?? {}) as {
          count?: unknown;
          kolNames?: unknown;
          email?: BroadcastPreviewData['email'];
        };
        const serverNames = Array.isArray(data.kolNames)
          ? data.kolNames.map((n) => String(n ?? '').trim()).filter(Boolean)
          : null;
        setBroadcastPreview((prev) => ({
          count: typeof data.count === 'number' && data.count > 0 ? data.count : kolIds.length,
          kolNames: serverNames ?? prev?.kolNames ?? [],
          email: data.email ?? null,
        }));
        setBroadcastPreviewError(
          data.email
            ? null
            : 'The sample email could not be rendered right now. You can still send the invitations.',
        );
      } catch (error) {
        if (broadcastPreviewRequestRef.current !== requestId) return;
        setBroadcastPreviewError(
          getApiErrorMessage(
            error,
            'The email preview could not be loaded. You can still send the invitations.',
          ),
        );
      } finally {
        if (broadcastPreviewRequestRef.current === requestId) setBroadcastPreviewLoading(false);
      }
    })();
  };

  const runBroadcast = async (kolIds: string[], source: BroadcastSource) => {
    const count = kolIds.length;
    const jid = job?.id ?? jobParam;
    try {
      setBroadcasting(true);
      await api.post(`/jobs/${encodeURIComponent(jid)}/broadcast`, { kolIds });
      await loadJob(); // Reload to get updated counts
      if (source === 'auto') setMatchedKOLs([]);
      setSelectedKOLIds([]);
      setShowManualSelector(false);
      setShowBroadcastMore(false);
      setActionNotice({
        type: 'success',
        message: `Invitations sent to ${count} KOL${count === 1 ? '' : 's'}. They can accept from their myKOL inbox.`,
      });
    } catch (error: any) {
      console.error('Broadcast failed:', error);
      setActionNotice({
        type: 'error',
        message: error.response?.data?.error || 'Failed to send invitations',
      });
    } finally {
      setBroadcasting(false);
    }
  };

  const confirmBroadcast = async () => {
    const pending = broadcastConfirm;
    if (!pending || broadcasting) return;
    try {
      await runBroadcast(pending.kolIds, pending.source);
    } finally {
      closeBroadcastConfirm();
    }
  };

  const handleBroadcast = () => {
    if (selectedKOLIds.length === 0) {
      setActionNotice({ type: 'error', message: 'Select at least one KOL to invite.' });
      return;
    }
    if (selectedKOLIds.length > BROADCAST_INVITE_BATCH_MAX) {
      setActionNotice({
        type: 'error',
        message: `Each send is limited to ${BROADCAST_INVITE_BATCH_MAX} invitations. Send this batch first, then select more.`,
      });
      return;
    }

    openBroadcastConfirm(selectedKOLIds, 'auto');
  };

  const handleManualBroadcast = (kolIds: string[]) => {
    if (kolIds.length === 0) {
      setActionNotice({ type: 'error', message: 'Select at least one KOL to invite.' });
      return;
    }
    if (kolIds.length > BROADCAST_INVITE_BATCH_MAX) {
      setActionNotice({
        type: 'error',
        message: `Each send is limited to ${BROADCAST_INVITE_BATCH_MAX} invitations. Send this batch first, then select fewer KOLs.`,
      });
      return;
    }

    // Close the picker so the confirm step is not stacked behind it.
    setShowManualSelector(false);
    openBroadcastConfirm(kolIds, 'manual');
  };

  const toggleKOLSelection = (kolId: string) => {
    setSelectedKOLIds((prev) => {
      if (prev.includes(kolId)) return prev.filter((id) => id !== kolId);
      if (prev.length >= BROADCAST_INVITE_BATCH_MAX) {
        setActionNotice({
          type: 'error',
          message: `You can invite up to ${BROADCAST_INVITE_BATCH_MAX} KOLs per send. Send this batch first, then invite more.`,
        });
        return prev;
      }
      return [...prev, kolId];
    });
  };

  const selectAllKOLs = () => {
    const capped = matchedKOLs.slice(0, BROADCAST_INVITE_BATCH_MAX);
    setSelectedKOLIds(capped.map((kol) => kol.id));
    if (matchedKOLs.length > BROADCAST_INVITE_BATCH_MAX) {
      setActionNotice({
        type: 'info',
        message: `Only the first ${BROADCAST_INVITE_BATCH_MAX} matches are selected — that is the maximum per send. Send them, then invite more.`,
      });
    }
  };

  const deselectAllKOLs = () => {
    setSelectedKOLIds([]);
  };

  const handleShortlist = async (invitationId: string, isShortlisted: boolean) => {
    try {
      await api.post(`/invitations/${invitationId}/shortlist`, { isShortlisted });
      await loadJob();
    } catch (error) {
      console.error('Failed to update shortlist:', error);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-12 text-glass-text-2">Loading...</div>;
  }

  if (!job) {
    return <div className="text-center py-12 text-glass-text-3">Campaign not found</div>;
  }

  const statusTooltips: Record<string, string> = {
    DRAFT: 'Campaign is being prepared. Not yet visible to KOLs.',
    OPEN: 'Campaign is active and accepting KOL applications.',
    ACTIVE: 'Campaign is in production with assigned KOLs.',
    PAUSED: 'Campaign is temporarily on hold.',
    COMPLETED: 'All deliverables have been submitted and approved.',
    ARCHIVED: 'Campaign is closed and archived.',
  };

  /** Draft jobs cannot send invitations; without an agency, KOL search APIs return nothing. */
  const canConfigureBroadcast =
    job.status !== 'DRAFT' &&
    !!job.managedByOrgId &&
    !['COMPLETED', 'ARCHIVED'].includes(job.status);

  const shortlistThreshold = Math.max(1, job.estimatedKOL || 0);
  const isShortlistReady = job.acceptedCount >= shortlistThreshold;
  
  const acceptedInvitations = invitations.filter(inv => inv.status === 'ACCEPTED');
  const shortlistedInvitations = invitations.filter(inv => inv.isShortlisted);
  const pendingInviteCount = invitations.filter((inv) =>
    ['PENDING', 'MODIFICATION_REQUESTED'].includes(inv.status),
  ).length;
  const declinedInviteCount = invitations.filter((inv) => inv.status === 'REJECTED').length;

  const allScriptsApproved =
    jobScripts.length > 0 && jobScripts.every((s: { status?: string }) => s.status === 'APPROVED_BY_BRAND');

  const nextStatuses = statusTransitions[job.status] || [];

  const mobilePrimaryAction: {
    label: string;
    onClick: () => void;
    disabled?: boolean;
  } | null = (() => {
    if (activeMainTab === 'brief') {
      if (job.status === 'DRAFT') {
        return {
          label: changingStatus ? 'Publishing…' : 'Publish campaign',
          onClick: () => void handleStatusChange('OPEN'),
          disabled: changingStatus,
        };
      }

      if (['COMPLETED', 'ARCHIVED'].includes(job.status)) return null;

      return {
        label: job.broadcastedCount > 0 ? 'Invite more KOLs' : 'Invite KOLs',
        onClick: () => {
          if (job.broadcastedCount > 0) setShowBroadcastMore(true);
          setActiveMainTab('broadcast');
        },
      };
    }

    if (activeMainTab !== 'broadcast' || ['COMPLETED', 'ARCHIVED'].includes(job.status)) {
      return null;
    }

    if (job.status === 'DRAFT') {
      return {
        label: changingStatus ? 'Publishing…' : 'Publish campaign',
        onClick: () => void handleStatusChange('OPEN'),
        disabled: changingStatus,
      };
    }

    if (!canConfigureBroadcast) return null;

    if (job.broadcastedCount > 0 && !showBroadcastMore) {
      return {
        label: 'Invite more KOLs',
        onClick: () => setShowBroadcastMore(true),
      };
    }

    if (selectionMode === 'manual') {
      if (selectedKOLIds.length === 0) {
        return {
          label: 'Select KOLs',
          onClick: () => setShowManualSelector(true),
        };
      }

      return {
        label: broadcasting ? 'Sending…' : `Broadcast to ${selectedKOLIds.length}`,
        onClick: () => void handleBroadcast(),
        disabled:
          broadcasting || selectedKOLIds.length > BROADCAST_INVITE_BATCH_MAX,
      };
    }

    if (!hasSearched || matchedKOLs.length === 0) {
      return {
        label: loadingMatches ? 'Finding matches…' : 'Find matching KOLs',
        onClick: () => void loadMatchingKOLs(),
        disabled: loadingMatches,
      };
    }

    return {
      label:
        selectedKOLIds.length > 0
          ? broadcasting
            ? 'Sending…'
            : `Send ${selectedKOLIds.length} invitation${selectedKOLIds.length === 1 ? '' : 's'}`
          : 'Select at least one KOL',
      onClick: () => void handleBroadcast(),
      disabled:
        broadcasting ||
        selectedKOLIds.length === 0 ||
        selectedKOLIds.length > BROADCAST_INVITE_BATCH_MAX,
    };
  })();

  const isCampaignToolTab = CAMPAIGN_TOOL_TABS.some((tab) => tab.value === activeMainTab);
  const campaignSteps = [
    { value: 'brief', label: 'Brief', done: true },
    {
      value: 'broadcast',
      label: 'Invite',
      done: job.broadcastedCount > 0,
    },
    {
      value: 'shortlist',
      label: 'Shortlist',
      done: isShortlistReady,
    },
    {
      value: 'scripts',
      label: 'Scripts',
      done: allScriptsApproved,
      badge: jobScripts.length ? String(jobScripts.length) : undefined,
    },
    { value: 'submissions', label: 'Production', done: false },
  ];

  return (
    <div className={`space-y-3 md:space-y-6 ${mobilePrimaryAction ? 'pb-16 md:pb-0' : ''}`}>
      {/* Breadcrumb */}
      <nav className="hidden md:flex items-center gap-1 text-sm text-glass-text-3">
        <button onClick={() => navigate('/agency/campaigns')} className="hover:text-accent2 transition-colors">Campaigns</button>
        <ChevronRight className="w-4 h-4" />
        <span className="text-glass-text font-medium truncate max-w-md">{job.title}</span>
      </nav>

      {/* Mobile detail bar and campaign stepper */}
      <div className="sticky top-0 z-30 -mx-4 -mt-4 border-b border-glass bg-glass-3 backdrop-blur-[28px] md:hidden">
        <div className="relative flex h-12 items-center gap-1 px-2">
          <button
            type="button"
            onClick={() => navigate('/agency/campaigns')}
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-glass-text-2 transition-colors hover:bg-glass-3 hover:text-accentText focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent2/60"
            aria-label="Back to campaigns"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="min-w-0 flex-1 px-1">
            <h1 className="truncate text-sm font-bold tracking-tight text-glass-text">{job.title}</h1>
            <p className="truncate text-[11px] text-glass-text-3">{job.brandOrg?.name || 'Campaign'}</p>
          </div>
          <span className={`${jobStatusPill(job.status)} shrink-0 text-[9px]`}>{job.status}</span>
          <button
            type="button"
            onClick={() => setShowMobileActions((open) => !open)}
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-glass-text-2 transition-colors hover:bg-glass-3 hover:text-accentText focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent2/60"
            aria-label="Campaign actions"
            aria-haspopup="menu"
            aria-expanded={showMobileActions}
          >
            <MoreHorizontal className="h-5 w-5" />
          </button>

          {showMobileActions && (
            <>
              <button
                type="button"
                className="fixed inset-0 z-30 cursor-default bg-transparent"
                onClick={() => setShowMobileActions(false)}
                tabIndex={-1}
                aria-hidden="true"
              />
              <div
                role="menu"
                className="glass-card absolute right-2 top-[calc(100%+0.5rem)] z-40 w-64 overflow-hidden p-1.5 shadow-glass"
              >
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setShowMobileActions(false);
                    void downloadCampaignPdf();
                  }}
                  className="flex min-h-11 w-full items-center gap-3 rounded-lg px-3 text-left text-sm font-medium text-glass-text-2 transition-colors hover:bg-glass-3 hover:text-glass-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent2/60"
                >
                  <FileDown className="h-4 w-4" />
                  Campaign PDF
                </button>
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setShowMobileActions(false);
                    navigate(`/agency/campaigns/${jobUrlSegment(job)}/edit`);
                  }}
                  className="flex min-h-11 w-full items-center gap-3 rounded-lg px-3 text-left text-sm font-medium text-glass-text-2 transition-colors hover:bg-glass-3 hover:text-glass-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent2/60"
                >
                  <Pencil className="h-4 w-4" />
                  Edit campaign
                </button>
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setShowMobileActions(false);
                    setShowEditCriteria(true);
                  }}
                  className="flex min-h-11 w-full items-center gap-3 rounded-lg px-3 text-left text-sm font-medium text-glass-text-2 transition-colors hover:bg-glass-3 hover:text-glass-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent2/60"
                >
                  <Settings className="h-4 w-4" />
                  Edit match criteria
                </button>
                {nextStatuses.length > 0 && <div className="my-1 border-t border-glass" />}
                {nextStatuses.map((status) => (
                  <button
                    key={status}
                    type="button"
                    role="menuitem"
                    disabled={changingStatus}
                    onClick={() => {
                      setShowMobileActions(false);
                      void handleStatusChange(status);
                    }}
                    className="flex min-h-11 w-full items-center rounded-lg px-3 text-left text-sm font-semibold text-accentText transition-colors hover:bg-accent2/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent2/60 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {changingStatus ? 'Updating…' : status === 'OPEN' ? 'Publish campaign' : status === 'ACTIVE' ? 'Activate campaign' : status === 'PAUSED' ? 'Pause campaign' : status === 'COMPLETED' ? 'Mark complete' : status === 'ARCHIVED' ? 'Archive campaign' : status}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        <div className="flex min-w-0 items-center gap-2 px-3 pb-2">
          <StepTabsList
            className="min-w-0 flex-1"
            activeValue={isCampaignToolTab ? '' : activeMainTab}
            onSelect={setActiveMainTab}
            steps={campaignSteps}
          />
          {!mobilePrimaryAction && (
            <label
              className={`relative flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border transition-colors focus-within:ring-2 focus-within:ring-accent2/60 ${
                isCampaignToolTab
                  ? 'border-accent2/30 bg-accent2/15 text-accentText'
                  : 'border-glass bg-glass-4 text-glass-text-2'
              }`}
              title="More campaign tools"
            >
              <Settings className="h-[18px] w-[18px]" aria-hidden />
              <select
                value={isCampaignToolTab ? activeMainTab : ''}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v) setActiveMainTab(v);
                }}
                className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                aria-label="More campaign tools"
              >
                <option value="">More tools</option>
                {CAMPAIGN_TOOL_TABS.map((tab) => (
                  <option key={tab.value} value={tab.value}>
                    {tab.label}
                  </option>
                ))}
              </select>
            </label>
          )}
        </div>
      </div>

      <div className="hidden md:flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/agency/campaigns')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="page-title">{job.title}</h1>
              <span className={jobStatusPill(job.status)} title={statusTooltips[job.status]}>
                {job.status}
              </span>
              <Button variant="outline" size="sm" className="flex items-center gap-1" onClick={downloadCampaignPdf}>
                <FileDown className="w-4 h-4" />
                Campaign PDF
              </Button>
            </div>
            <p className="text-glass-text-2 mt-1">{job.brandOrg?.name}</p>
          </div>
        </div>
        {nextStatuses.length > 0 && (
          <div className="flex items-center gap-2">
            {nextStatuses.map((status) => (
              <Button
                key={status}
                variant={status === 'OPEN' || status === 'ACTIVE' ? 'default' : 'outline'}
                size="sm"
                disabled={changingStatus}
                onClick={() => handleStatusChange(status)}
                title={statusTooltips[status]}
              >
                {changingStatus ? 'Updating...' : status === 'OPEN' ? 'Publish Campaign' : status === 'ACTIVE' ? 'Activate' : status === 'PAUSED' ? 'Pause' : status === 'COMPLETED' ? 'Mark Complete' : status === 'ARCHIVED' ? 'Archive' : status}
              </Button>
            ))}
          </div>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-4 gap-1.5 md:gap-4">
        {[
          { label: 'Broadcasted', mobileLabel: 'Sent', value: job.broadcastedCount, tone: 'accent' },
          { label: 'Accepted', value: job.acceptedCount, tone: 'emerald' },
          { label: 'Shortlisted', mobileLabel: 'Shortlist', value: job.shortlistedCount, tone: 'purple' },
          { label: 'Approved', value: job.approvedTalentCount, tone: 'amber' },
        ].map((s) => {
          const toneClass =
            s.tone === 'accent'
              ? 'text-accentText md:text-accent2'
              : s.tone === 'emerald'
              ? 'text-emerald-500 dark:text-emerald-300'
              : s.tone === 'purple'
              ? 'text-purple-500 dark:text-purple-300'
              : 'text-amber-500 dark:text-amber-300';
          return (
            <div key={s.label} className="glass-card min-w-0 p-2 hover:-translate-y-0.5 hover:border-accent2/40 transition-all duration-150 md:p-5">
              <div className={`stat-display truncate text-lg md:text-[1.85rem] ${toneClass}`}>{s.value}</div>
              <div className="mt-0.5 truncate text-[9px] font-medium uppercase tracking-[0.04em] text-glass-text-3 md:mt-1 md:text-[0.7rem] md:tracking-[0.05em]">
                <span className="md:hidden">{s.mobileLabel || s.label}</span>
                <span className="hidden md:inline">{s.label}</span>
              </div>
            </div>
          );
        })}
      </div>

      {actionNotice && (
        <div
          role="status"
          className={`rounded-xl border p-4 flex items-start justify-between gap-3 ${
            actionNotice.type === 'success'
              ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-800 dark:text-emerald-200'
              : actionNotice.type === 'error'
                ? 'border-rose-500/30 bg-rose-500/10 text-rose-800 dark:text-rose-200'
                : 'border-accent2/30 bg-accent2/10 text-accentText md:text-accent2'
          }`}
        >
          <p className="text-sm font-medium">{actionNotice.message}</p>
          <button
            type="button"
            onClick={() => setActionNotice(null)}
            className="shrink-0 opacity-70 hover:opacity-100"
            aria-label="Dismiss notification"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {job.status === 'DRAFT' && job.broadcastedCount === 0 && (
        <div className="rounded-xl border border-accent2/30 bg-accent2/10 p-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-sm font-medium text-accentText md:text-accent2">Publish to invite KOLs</p>
            <p className="text-xs text-glass-text-2 mt-1">
              Drafts stay private. KOLs only see this campaign after you send invitations.
            </p>
          </div>
          <Button
            size="sm"
            disabled={changingStatus}
            onClick={() => handleStatusChange('OPEN')}
            className={mobilePrimaryAction ? 'hidden md:inline-flex' : undefined}
          >
            {changingStatus ? 'Publishing...' : 'Publish campaign'}
          </Button>
        </div>
      )}
      {job.status !== 'DRAFT' &&
        job.broadcastedCount === 0 &&
        !['COMPLETED', 'ARCHIVED'].includes(job.status) &&
        !actionNotice && (
        <div className="rounded-xl border border-accent2/30 bg-accent2/10 p-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-sm font-medium text-accentText md:text-accent2">Invite from your roster</p>
            <p className="text-xs text-glass-text-2 mt-1">
              {(job.jobTags?.length ?? 0) > 0
                ? 'Auto-match by tags, or pick people yourself.'
                : 'No tags on this brief — pick KOLs from your roster.'}
            </p>
          </div>
          <Button
            size="sm"
            className={mobilePrimaryAction ? 'hidden md:inline-flex' : undefined}
            onClick={() => {
              setActiveMainTab('broadcast');
              setSelectionMode('manual');
              if (canConfigureBroadcast) setShowManualSelector(true);
            }}
          >
            Invite from roster
          </Button>
        </div>
      )}
      {job.acceptedCount > 0 && job.shortlistedCount === 0 && !actionNotice && (
        <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm font-medium text-emerald-600 dark:text-emerald-300">
            {job.acceptedCount} KOL{job.acceptedCount !== 1 ? 's have' : ' has'} accepted. Shortlist who goes to production.
          </p>
          <Button size="sm" variant="outline" onClick={() => setActiveMainTab('shortlist')}>
            Open shortlist
          </Button>
        </div>
      )}

      <Tabs defaultValue="brief" value={activeMainTab} onValueChange={setActiveMainTab}>
        <div className="hidden min-w-0 flex-nowrap items-center gap-2 md:flex md:flex-wrap md:justify-between md:gap-3">
          <StepTabsList
            className="min-w-0 flex-1"
            activeValue={isCampaignToolTab ? '' : activeMainTab}
            onSelect={setActiveMainTab}
            steps={campaignSteps}
          />
          <label className="hidden items-center gap-2 text-sm text-glass-text-2 md:flex">
            <span className="sr-only">More campaign tools</span>
            <Select
              value={isCampaignToolTab ? activeMainTab : ''}
              onChange={(e) => {
                const v = e.target.value;
                if (v) setActiveMainTab(v);
              }}
              className="w-44"
              aria-label="More campaign tools"
            >
              <option value="">More tools</option>
              {CAMPAIGN_TOOL_TABS.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </Select>
          </label>
        </div>

        {/* Brief Tab */}
        <TabsContent value="brief">
          <Card>
            <CardContent className="p-4 space-y-5 md:p-6 md:space-y-6">
              <div className="flex items-center justify-between pb-4 border-b border-glass">
                <h3 className="text-lg font-semibold text-glass-text">Campaign Brief</h3>
                <Button variant="outline" size="sm" onClick={() => navigate(`/agency/campaigns/${jobUrlSegment(job)}/edit`)}>
                  Edit Brief
                </Button>
              </div>
              <div>
                <h3 className="font-semibold text-glass-text mb-2">Description</h3>
                <p className="text-glass-text-2 whitespace-pre-wrap">{job.description}</p>
              </div>

              <GoogleWorkspaceJobPanel
                jobId={job.id}
                onBriefImported={loadJob}
                showUpdateBanner
                className="border-0 bg-transparent shadow-none"
              />

              {(job.objectives || job.targetAudience || job.keyMessages) && (
                <div className="grid md:grid-cols-1 gap-4 border-t border-glass pt-4">
                  {job.objectives && (
                    <div>
                      <h3 className="font-semibold text-glass-text mb-1">Objectives</h3>
                      <p className="text-glass-text-2 whitespace-pre-wrap text-sm">{job.objectives}</p>
                    </div>
                  )}
                  {job.targetAudience && (
                    <div>
                      <h3 className="font-semibold text-glass-text mb-1">Target audience</h3>
                      <p className="text-glass-text-2 whitespace-pre-wrap text-sm">{job.targetAudience}</p>
                    </div>
                  )}
                  {job.keyMessages && (
                    <div>
                      <h3 className="font-semibold text-glass-text mb-1">Key messages</h3>
                      <p className="text-glass-text-2 whitespace-pre-wrap text-sm">{job.keyMessages}</p>
                    </div>
                  )}
                </div>
              )}

              {job.contentTypes && Array.isArray(job.contentTypes) && job.contentTypes.length > 0 && (
                <div>
                  <h3 className="font-semibold text-glass-text mb-2">Content types</h3>
                  <div className="flex flex-wrap gap-2">
                    {(job.contentTypes as string[]).map((c) => (
                      <Badge key={c} variant="secondary">{c}</Badge>
                    ))}
                  </div>
                </div>
              )}

              {job.hashtags && Array.isArray(job.hashtags) && (job.hashtags as string[]).length > 0 && (
                <div>
                  <h3 className="font-semibold text-glass-text mb-2">Hashtags</h3>
                  <p className="text-sm text-accentText md:text-accent2">{(job.hashtags as string[]).join(' ')}</p>
                </div>
              )}

              {job.complianceRequirements && (
                <div>
                  <h3 className="font-semibold text-glass-text mb-1">Compliance</h3>
                  <p className="text-glass-text-2 whitespace-pre-wrap text-sm">{job.complianceRequirements}</p>
                </div>
              )}

              <div className="border-t border-glass pt-4">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    className="mt-1 rounded border-glass accent-accent2"
                    checked={requireComplianceGate}
                    disabled={savingComplianceGate}
                    onChange={(e) => {
                      const v = e.target.checked;
                      setRequireComplianceGate(v);
                      saveComplianceGateSetting(v);
                    }}
                  />
                  <span>
                    <span className="font-semibold text-glass-text">Require compliance approval before broadcast</span>
                    <span className="block text-sm text-glass-text-2 mt-1">
                      When enabled, the checklist in the Operations tab must be marked approved before invitations can be sent.
                    </span>
                  </span>
                </label>
                <label className="flex items-start gap-3 cursor-pointer mt-3">
                  <input
                    type="checkbox"
                    className="mt-1 rounded border-glass accent-accent2"
                    checked={requireContractGate}
                    disabled={savingContractGate}
                    onChange={(e) => {
                      const v = e.target.checked;
                      setRequireContractGate(v);
                      saveContractGateSetting(v);
                    }}
                  />
                  <span>
                    <span className="font-semibold text-glass-text">Require contract before broadcast</span>
                    <span className="block text-sm text-glass-text-2 mt-1">
                      When enabled, the Contracts tab appears before Broadcast so you can prepare contracts for KOLs to review alongside their invitation.
                    </span>
                  </span>
                </label>
              </div>

              {job.deliverablesChecklist && Array.isArray(job.deliverablesChecklist) && (
                <div>
                  <h3 className="font-semibold text-glass-text mb-2">Deliverables</h3>
                  <ul className="list-disc list-inside text-sm text-glass-text-2">
                    {(job.deliverablesChecklist as { label?: string }[]).map((d, i) => (
                      <li key={i}>{d.label || JSON.stringify(d)}</li>
                    ))}
                  </ul>
                </div>
              )}

              {job.demoVideoLink && (
                <div>
                  <h3 className="font-semibold text-glass-text mb-2">Demo Video</h3>
                  <a href={job.demoVideoLink} target="_blank" rel="noopener noreferrer" className="text-accentText hover:underline md:text-accent2">
                    {job.demoVideoLink}
                  </a>
                </div>
              )}

              {job.exampleScript && (
                <div>
                  <h3 className="font-semibold text-glass-text mb-2">Example Script (To be discussed in Script Studio)</h3>
                  <p className="text-glass-text-2 whitespace-pre-wrap">{job.exampleScript}</p>
                </div>
              )}

              {job.rewardsJson && (
                <div>
                  <h3 className="font-semibold text-glass-text mb-2">Rewards</h3>
                  <div className="grid grid-cols-3 gap-4">
                    {(job.rewardsJson as any).cash > 0 && (
                      <div className="p-3 rounded-xl border border-emerald-500/20 bg-emerald-500/10">
                        <div className="text-xs text-glass-text-3 uppercase tracking-[0.05em]">Cash</div>
                        <div className="text-lg font-semibold text-emerald-500 dark:text-emerald-300">RM {(job.rewardsJson as any).cash}</div>
                      </div>
                    )}
                    {(job.rewardsJson as any).product && (
                      <div className="p-3 rounded-xl border border-accent2/20 bg-accent2/10">
                        <div className="text-xs text-glass-text-3 uppercase tracking-[0.05em]">Product</div>
                        <div className="text-lg font-semibold text-accentText md:text-accent2">{(job.rewardsJson as any).product}</div>
                      </div>
                    )}
                    {(job.rewardsJson as any).commission > 0 && (
                      <div className="p-3 rounded-xl border border-purple-500/20 bg-purple-500/10">
                        <div className="text-xs text-glass-text-3 uppercase tracking-[0.05em]">Commission</div>
                        <div className="text-lg font-semibold text-purple-500 dark:text-purple-300">{(job.rewardsJson as any).commission}%</div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {job.products && job.products.length > 0 && (
                <div>
                  <h3 className="font-semibold text-glass-text mb-2">Products</h3>
                  <div className="space-y-3">
                    {job.products.map((product) => (
                      <div key={product.id} className="p-4 rounded-xl border border-glass bg-glass-2">
                        <div className="font-medium text-glass-text">{product.title}</div>
                        {product.description && (
                          <p className="text-sm text-glass-text-2 mt-1">{product.description}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Invite Tab (matching criteria + send) */}
        <TabsContent value="broadcast">
          <Card>
            <CardContent className="p-6 space-y-6">
              <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-glass bg-glass-2 px-4 py-3">
                <p className="text-sm text-glass-text-2">
                  {(job.jobTags?.length ?? 0) > 0
                    ? `${job.jobTags!.length} tag${job.jobTags!.length === 1 ? '' : 's'} · target ${job.estimatedKOL} KOLs`
                    : `No tags · target ${job.estimatedKOL} KOLs — pick from your roster`}
                </p>
                <Button variant="outline" size="sm" onClick={() => setShowEditCriteria(true)}>
                  <Settings className="w-4 h-4 mr-1" />
                  Edit criteria
                </Button>
              </div>
              {job.status === 'DRAFT' && (
                <div className="rounded-xl border border-amber-400/30 bg-amber-400/10 p-4 text-sm text-amber-800 dark:text-amber-200 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <strong>Publish first.</strong> Drafts cannot send invitations, and KOL search has no live campaign scope.
                  </div>
                  <Button
                    size="sm"
                    disabled={changingStatus}
                    onClick={() => handleStatusChange('OPEN')}
                    className="hidden md:inline-flex"
                  >
                    {changingStatus ? 'Publishing...' : 'Publish campaign'}
                  </Button>
                </div>
              )}
              {!job.managedByOrgId && (
                <div className="rounded-xl border border-rose-500/30 bg-rose-500/10 p-4 text-sm text-rose-800 dark:text-rose-200">
                  <strong>No managing agency on this job.</strong> Assign an agency organization to the campaign before
                  searching or inviting KOLs.
                </div>
              )}
              {job.requireComplianceBeforeBroadcast && !complianceApproved && job.broadcastedCount === 0 && (
                <div className="rounded-xl border border-amber-400/30 bg-amber-400/10 p-4 text-sm text-amber-500 dark:text-amber-300">
                  <strong>Compliance gate is on.</strong> Complete and approve the checklist in the{' '}
                  <strong>Operations</strong> tab before broadcasting.
                </div>
              )}
              {/* Broadcast summary when invitations have been sent */}
              {job.broadcastedCount > 0 && !showBroadcastMore && (
                <div className="text-center py-6">
                  <CheckCircle className="w-12 h-12 mx-auto text-emerald-500 dark:text-emerald-300 mb-3" />
                  <h3 className="text-xl font-semibold text-glass-text mb-2">Invite summary</h3>
                  <p className="text-glass-text-2 mb-4">
                    {job.broadcastedCount} invitation{job.broadcastedCount !== 1 ? 's' : ''} sent so far
                  </p>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-3xl mx-auto mb-6">
                    <div className="p-4 rounded-xl border border-accent2/20 bg-accent2/10">
                      <div className="text-2xl font-semibold tracking-tightest text-accentText md:text-accent2">{job.broadcastedCount}</div>
                      <div className="text-xs text-glass-text-3 uppercase tracking-[0.05em] mt-1">Sent</div>
                    </div>
                    <div className="p-4 rounded-xl border border-emerald-500/20 bg-emerald-500/10">
                      <div className="text-2xl font-semibold tracking-tightest text-emerald-500 dark:text-emerald-300">{job.acceptedCount}</div>
                      <div className="text-xs text-glass-text-3 uppercase tracking-[0.05em] mt-1">Accepted</div>
                    </div>
                    <div className="p-4 rounded-xl border border-amber-400/20 bg-amber-400/10">
                      <div className="text-2xl font-semibold tracking-tightest text-amber-600 dark:text-amber-300">
                        {pendingInviteCount}
                      </div>
                      <div className="text-xs text-glass-text-3 uppercase tracking-[0.05em] mt-1">Pending</div>
                    </div>
                    <div className="p-4 rounded-xl border border-rose-500/20 bg-rose-500/10">
                      <div className="text-2xl font-semibold tracking-tightest text-rose-500 dark:text-rose-300">
                        {declinedInviteCount}
                      </div>
                      <div className="text-xs text-glass-text-3 uppercase tracking-[0.05em] mt-1">Declined</div>
                    </div>
                  </div>
                  {!['COMPLETED', 'ARCHIVED'].includes(job.status) && (
                    <Button onClick={() => setShowBroadcastMore(true)} variant="outline" size="lg" className="hidden items-center gap-2 mx-auto md:inline-flex">
                      <Sparkles className="w-4 h-4" />
                      Invite more KOLs
                    </Button>
                  )}
                </div>
              )}

              {(job.broadcastedCount === 0 || showBroadcastMore) &&
                !['COMPLETED', 'ARCHIVED'].includes(job.status) &&
                canConfigureBroadcast && (
                <>
                  {showBroadcastMore && (
                    <div className="rounded-xl border border-accent2/30 bg-accent2/10 p-3 text-sm text-accentText md:text-accent2 flex items-center justify-between">
                      <span>Additional broadcast round — {job.broadcastedCount} already sent</span>
                      <Button variant="ghost" size="sm" onClick={() => setShowBroadcastMore(false)}>Cancel</Button>
                    </div>
                  )}
                  {/* Mode Selection */}
                  <div className="flex items-center justify-center gap-4 pb-6 border-b border-glass">
                    <button
                      onClick={() => setSelectionMode('auto')}
                      className={`flex items-center gap-3 px-6 py-4 rounded-xl border-2 transition-all ${
                        selectionMode === 'auto'
                          ? 'border-accent2 bg-accent2/10'
                          : 'border-glass hover:border-glass-strong bg-glass-2'
                      }`}
                    >
                      <Sparkles className={`w-6 h-6 ${selectionMode === 'auto' ? 'text-accentText md:text-accent2' : 'text-glass-text-3'}`} />
                      <div className="text-left">
                        <div className={`font-semibold ${selectionMode === 'auto' ? 'text-accentText md:text-accent2' : 'text-glass-text'}`}>
                          Auto-match
                        </div>
                        <div className="text-sm text-glass-text-2">By tags and demographics</div>
                      </div>
                    </button>
                    <button
                      onClick={() => setSelectionMode('manual')}
                      className={`flex items-center gap-3 px-6 py-4 rounded-xl border-2 transition-all ${
                        selectionMode === 'manual'
                          ? 'border-accent2 bg-accent2/10'
                          : 'border-glass hover:border-glass-strong bg-glass-2'
                      }`}
                    >
                      <UserSearch className={`w-6 h-6 ${selectionMode === 'manual' ? 'text-accentText md:text-accent2' : 'text-glass-text-3'}`} />
                      <div className="text-left">
                        <div className={`font-semibold ${selectionMode === 'manual' ? 'text-accentText md:text-accent2' : 'text-glass-text'}`}>
                          Manual select
                        </div>
                        <div className="text-sm text-glass-text-2">Pick from your roster</div>
                      </div>
                    </button>
                  </div>

                  {selectionMode === 'auto' ? (
                    !hasSearched ? (
                      <div className="text-center py-8">
                        <Sparkles className="w-16 h-16 mx-auto text-accentText mb-4 md:text-accent2" />
                        <h3 className="text-xl font-semibold text-glass-text mb-2">AI-Powered Matching</h3>
                        <p className="text-glass-text-2 mb-6">
                          Our algorithm will find KOLs that match your criteria
                        </p>
                        <Button
                          type="button"
                          onClick={() => loadMatchingKOLs()}
                          disabled={loadingMatches}
                          size="lg"
                          className="hidden md:inline-flex"
                        >
                          {loadingMatches ? 'Finding matches...' : 'Find Matching KOLs'}
                        </Button>
                      </div>
                    ) : loadingMatches ? (
                      <div className="flex items-center justify-center py-16">
                        <div className="text-center">
                          <div className="animate-spin rounded-full h-16 w-16 border-4 border-accent2 border-t-transparent mx-auto mb-4"></div>
                          <p className="text-glass-text-2">Finding matching KOLs...</p>
                        </div>
                      </div>
                    ) : matchedKOLs.length === 0 ? (
                      <div className="text-center py-8">
                        <div className="w-16 h-16 mx-auto bg-amber-400/15 rounded-full flex items-center justify-center mb-4">
                          <UserSearch className="h-8 w-8 text-amber-600 dark:text-amber-300" aria-hidden />
                        </div>
                        <h3 className="text-xl font-semibold text-glass-text mb-2">No Matching KOLs Found</h3>
                        <p className="text-glass-text-2 mb-4">
                          The criteria might be too strict. Try one of these options:
                        </p>
                        <div className="max-w-md mx-auto space-y-3 mb-6">
                          <div className="p-3 rounded-xl border border-accent2/20 bg-accent2/10 text-left">
                            <p className="flex items-center gap-1.5 text-sm text-accentText font-medium md:text-accent2">
                              <UserSearch className="h-4 w-4" aria-hidden />
                              Use manual selection
                            </p>
                            <p className="text-xs text-glass-text-2 mt-1">Browse all KOLs with flexible filters</p>
                          </div>
                          <div className="p-3 rounded-xl border border-purple-500/20 bg-purple-500/10 text-left">
                            <p className="flex items-center gap-1.5 text-sm text-purple-500 dark:text-purple-300 font-medium">
                              <Settings className="h-4 w-4" aria-hidden />
                              Edit match criteria
                            </p>
                            <p className="text-xs text-glass-text-2 mt-1">Adjust tags or remove demographic filters</p>
                          </div>
                        </div>
                        <div className="flex items-center justify-center gap-3">
                          <Button
                            onClick={() => setSelectionMode('manual')}
                            size="lg"
                          >
                            Try Manual Selection
                          </Button>
                          <Button
                            onClick={() => loadMatchingKOLs()}
                            variant="outline"
                            size="lg"
                          >
                            Try Again
                          </Button>
                        </div>
                      </div>
                    ) : (
                    <>
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="text-lg font-semibold text-glass-text">
                            Found {matchedKOLs.length} Matching KOLs
                            {aiEnhanced && (
                              <span className="ml-2 inline-flex items-center gap-1 text-sm font-normal text-purple-500 dark:text-purple-300 bg-purple-500/10 border border-purple-500/30 px-2 py-0.5 rounded-full">
                                <Brain className="w-3 h-3" /> AI Enhanced
                              </span>
                            )}
                          </h3>
                          <p className="text-sm text-glass-text-2">
                            {selectedKOLIds.length} selected for broadcast (max {BROADCAST_INVITE_BATCH_MAX} per send)
                          </p>
                        </div>
                        <div className="flex gap-2">
                          {!aiEnhanced && !aiRanking && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={loadAIEnhancedMatches}
                              className="flex items-center gap-1"
                            >
                              <Brain className="w-4 h-4" />
                              Enhance with AI
                            </Button>
                          )}
                          {aiRanking && (
                            <Button variant="outline" size="sm" disabled className="flex items-center gap-1">
                              <Loader2 className="w-4 h-4 animate-spin" />
                              AI Analyzing...
                            </Button>
                          )}
                          <Button
                            variant="outline"
                            size="sm"
                            title={`Selects up to ${BROADCAST_INVITE_BATCH_MAX} matches per send`}
                            onClick={selectAllKOLs}
                          >
                            Select first {BROADCAST_INVITE_BATCH_MAX}
                          </Button>
                          <Button variant="outline" size="sm" onClick={deselectAllKOLs}>
                            Deselect All
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => { setMatchedKOLs([]); setAiEnhanced(false); setAiError(null); }}>
                            Cancel
                          </Button>
                        </div>
                      </div>

                      {aiError && (
                        <div className="flex items-start gap-2 text-sm text-amber-500 dark:text-amber-300 bg-amber-400/10 border border-amber-400/30 rounded-xl p-3">
                          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                          <span>{aiError}</span>
                        </div>
                      )}

                      <div className="rounded-xl border border-glass divide-y divide-glass max-h-96 overflow-y-auto bg-glass-2">
                        {matchedKOLs.map((kol) => {
                          const isSelected = selectedKOLIds.includes(kol.id);
                          const platforms = kol.platforms || {};
                          const totalFollowers = Object.values(platforms as any).reduce(
                            (sum: number, p: any) => sum + (p?.followers || 0),
                            0
                          );

                          const getScoreColor = (score: number) => {
                            if (score >= 70) return 'bg-emerald-500/15 text-emerald-500 dark:text-emerald-300 border-emerald-500/30';
                            if (score >= 50) return 'bg-accent2/15 text-accentText md:text-accent2 border-accent2/30';
                            if (score >= 30) return 'bg-amber-400/15 text-amber-500 dark:text-amber-300 border-amber-400/30';
                            return 'bg-glass-3 text-glass-text-2 border-glass';
                          };

                          const displayScore = kol.finalScore ?? kol.matchScore;

                          return (
                            <div
                              key={kol.id}
                              className={`p-4 cursor-pointer transition-colors ${
                                isSelected ? 'bg-accent2/10' : 'hover:bg-glass-3'
                              }`}
                              onClick={() => toggleKOLSelection(kol.id)}
                            >
                              <div className="flex items-start gap-4">
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => toggleKOLSelection(kol.id)}
                                  onClick={(e) => e.stopPropagation()}
                                  className="mt-1 w-4 h-4 rounded border-glass accent-accent2"
                                />
                                <div className="flex-1">
                                  <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                      <div className="flex items-center gap-2">
                                        <h4 className="font-medium text-glass-text">{kol.name}</h4>
                                        {displayScore != null && (
                                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold border ${getScoreColor(displayScore)}`}>
                                            {displayScore}/100
                                          </span>
                                        )}
                                        {kol.aiScore != null && (
                                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-purple-500/10 text-purple-500 dark:text-purple-300 border border-purple-500/30" title="AI confidence score">
                                            <Brain className="w-3 h-3" /> {kol.aiScore}
                                          </span>
                                        )}
                                      </div>
                                      <p className="text-sm text-glass-text-3">{kol.email}</p>

                                      {/* AI reasons */}
                                      {kol.aiReasons && kol.aiReasons.length > 0 && (
                                        <div className="mt-2 text-xs space-y-0.5">
                                          <div className="font-medium text-purple-500 dark:text-purple-300 flex items-center gap-1"><Brain className="w-3 h-3" /> AI Insights:</div>
                                          <div className="flex flex-wrap gap-1">
                                            {kol.aiReasons.map((reason: string, idx: number) => (
                                              <span key={idx} className="inline-block bg-purple-500/10 text-purple-500 dark:text-purple-300 px-2 py-0.5 rounded border border-purple-500/20">
                                                {reason}
                                              </span>
                                            ))}
                                          </div>
                                        </div>
                                      )}

                                      {/* Risk flags */}
                                      {kol.riskFlags && kol.riskFlags.length > 0 && (
                                        <div className="mt-1 flex flex-wrap gap-1">
                                          {kol.riskFlags.map((flag: string, idx: number) => (
                                            <span key={idx} className="inline-flex items-center gap-1 text-xs bg-amber-400/10 text-amber-500 dark:text-amber-300 px-2 py-0.5 rounded border border-amber-400/20">
                                              <AlertTriangle className="w-3 h-3" /> {flag}
                                            </span>
                                          ))}
                                        </div>
                                      )}

                                      {/* Deterministic match reasons */}
                                      {kol.matchReasons && kol.matchReasons.length > 0 && (
                                        <div className="mt-2 text-xs text-glass-text-2 space-y-0.5">
                                          <div className="font-medium text-glass-text">Match Details:</div>
                                          <div className="flex flex-wrap gap-1">
                                            {kol.matchReasons.map((reason: string, idx: number) => (
                                              <span key={idx} className="inline-block bg-glass-3 text-glass-text-2 px-2 py-0.5 rounded border border-glass">
                                                {reason}
                                              </span>
                                            ))}
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                    <div className="text-right ml-4">
                                      <div className="text-sm font-medium text-glass-text">
                                        {totalFollowers.toLocaleString()} followers
                                      </div>
                                      <div className="text-xs text-glass-text-3">Total reach</div>
                                    </div>
                                  </div>
                                  {kol.tags && kol.tags.length > 0 && (
                                    <div className="flex flex-wrap gap-1 mt-2">
                                      {kol.tags.map((tagRel: any) => (
                                        <Badge key={tagRel.tag?.id || tagRel.tagId} variant="secondary" className="text-xs">
                                          {tagRel.tag?.name || tagRel.tagId}
                                        </Badge>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      <div className="rounded-xl border border-accent2/30 bg-accent2/10 p-4">
                        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                          <div>
                            <h4 className="font-semibold text-accentText md:text-accent2">
                              Ready to send {selectedKOLIds.length} invitation
                              {selectedKOLIds.length !== 1 ? 's' : ''}?
                            </h4>
                            <p className="text-sm text-glass-text-2 mt-1">
                              Selected KOLs will receive job invitations immediately. Maximum{' '}
                              {BROADCAST_INVITE_BATCH_MAX} per send — use another broadcast round for more.
                            </p>
                          </div>
                          <Button
                            onClick={handleBroadcast}
                            disabled={
                              broadcasting ||
                              selectedKOLIds.length === 0 ||
                              selectedKOLIds.length > BROADCAST_INVITE_BATCH_MAX
                            }
                            size="lg"
                            className="hidden shrink-0 md:inline-flex"
                          >
                            {broadcasting ? 'Sending...' : `Send ${selectedKOLIds.length} Invitations`}
                          </Button>
                        </div>
                      </div>
                    </>
                  )) : (
                    /* Manual Selection Mode */
                    <div className="text-center py-8">
                      <UserSearch className="w-16 h-16 mx-auto text-accentText mb-4 md:text-accent2" />
                      <h3 className="text-xl font-semibold text-glass-text mb-2">Manual KOL Selection</h3>
                      <p className="text-glass-text-2 mb-6">
                        Browse and filter all KOLs with advanced search options
                      </p>
                      <Button
                        type="button"
                        onClick={() => setShowManualSelector(true)}
                        size="lg"
                        className="hidden md:inline-flex"
                      >
                        Open Manual Selector
                      </Button>
                      {selectedKOLIds.length > 0 && (
                        <div className="mt-6 rounded-xl border border-accent2/30 bg-accent2/10 p-4 max-w-lg mx-auto">
                          <div className="flex items-center justify-between">
                            <div className="text-left">
                              <h4 className="font-semibold text-accentText md:text-accent2">
                                {selectedKOLIds.length} KOL{selectedKOLIds.length !== 1 ? 's' : ''} selected
                              </h4>
                              <p className="text-sm text-glass-text-2 mt-1">
                                Maximum {BROADCAST_INVITE_BATCH_MAX} invitations per send. Run broadcast again to reach more KOLs.
                              </p>
                            </div>
                            <Button
                              onClick={handleBroadcast}
                              disabled={
                                broadcasting ||
                                selectedKOLIds.length === 0 ||
                                selectedKOLIds.length > BROADCAST_INVITE_BATCH_MAX
                              }
                              size="lg"
                              className="hidden md:inline-flex"
                            >
                              {broadcasting ? 'Sending...' : `Broadcast to ${selectedKOLIds.length}`}
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}

              {invitations.length > 0 && (
                <div>
                  <h3 className="font-semibold text-glass-text mb-3">All Invitations</h3>
                  <DataTable
                    data={invitations}
                    columns={[
                      {
                        header: 'KOL',
                        accessor: (row: Invitation) => row.kolProfile?.name || '',
                      },
                      {
                        header: 'Status',
                        accessor: (row: Invitation) => (
                          <span className={inviteStatusPill(row.status)}>{row.status}</span>
                        ),
                      },
                      {
                        header: 'Sent',
                        accessor: (row: Invitation) => formatDate(row.broadcastedAt || row.createdAt),
                      },
                    ]}
                    onRowClick={(inv) => navigate(`/agency/invitations/${toSlug(inv.kolProfile?.name || 'invitation', inv.id)}`)}
                  />
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Shortlist Tab */}
        <TabsContent value="shortlist">
          <Card>
            <CardContent className="p-6 space-y-6">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <h2 className="text-lg font-semibold text-glass-text">Shortlist</h2>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="gap-2"
                  disabled={shortlistRefreshing}
                  onClick={() => void refreshShortlist()}
                >
                  <RefreshCw className={`h-4 w-4 ${shortlistRefreshing ? 'animate-spin' : ''}`} />
                  Refresh shortlist
                </Button>
              </div>
              {!isShortlistReady ? (
                <div className="text-center py-8 text-glass-text-3">
                  <p className="mb-2 text-glass-text-2">Waiting for more acceptances...</p>
                  <p className="text-sm">
                    Need {shortlistThreshold} accepted to choose the production shortlist; currently have {job.acceptedCount}
                  </p>
                </div>
              ) : (
                <>
                  <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-4">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-emerald-500 dark:text-emerald-300" />
                      <span className="font-medium text-emerald-500 dark:text-emerald-300">Shortlist Ready!</span>
                    </div>
                    <p className="text-sm text-glass-text-2 mt-1">
                      You have {job.acceptedCount} accepted invitations. Select {shortlistThreshold} KOLs for production.
                    </p>
                  </div>

                  {shortlistedInvitations.length > 0 && (
                    <div>
                      <h3 className="font-semibold text-glass-text mb-2">Shortlist order and notes</h3>
                      <p className="text-xs text-glass-text-3 mb-3">Drag to set priority rank. Notes save on blur.</p>
                      <ShortlistRanker jobId={job.id} invitations={shortlistedInvitations} onUpdated={loadJob} />
                    </div>
                  )}

                  <div>
                    <h3 className="font-semibold text-glass-text mb-3">Accepted KOLs</h3>
                    <DataTable
                      data={acceptedInvitations}
                      columns={[
                        {
                          header: 'KOL',
                          accessor: (row: Invitation) => row.kolProfile?.name || '',
                        },
                        {
                          header: 'Status',
                          accessor: (row: Invitation) => (
                            <span
                              className={
                                row.isShortlisted
                                  ? 'status-pill status-pill-pos'
                                  : 'status-pill status-pill-warn'
                              }
                            >
                              {row.isShortlisted ? 'Shortlisted' : 'Accepted'}
                            </span>
                          ),
                        },
                        {
                          header: 'Action',
                          accessor: (row: Invitation) => (
                            <Button
                              size="sm"
                              variant={row.isShortlisted ? 'outline' : 'default'}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleShortlist(row.id, !row.isShortlisted);
                              }}
                            >
                              {row.isShortlisted ? 'Remove' : 'Shortlist'}
                            </Button>
                          ),
                        },
                      ]}
                      onRowClick={(inv) => navigate(`/agency/invitations/${toSlug(inv.kolProfile?.name || 'invitation', inv.id)}`)}
                    />
                  </div>

                  {shortlistedInvitations.length >= (job.estimatedKOL || 0) && (
                    <div className="rounded-xl border border-accent2/30 bg-accent2/10 p-4">
                      <p className="text-sm text-accentText md:text-accent2">
                        ✓ You've shortlisted {shortlistedInvitations.length} KOLs. The brand can now approve for production.
                      </p>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Scripts Tab — same summary as /agency/scripts */}
        <TabsContent value="scripts">
          <AgencyJobScriptsSummary
            jobId={job.id}
            jobTitle={job?.title || 'script'}
            invitations={invitations}
            jobScripts={jobScripts}
            onRefresh={() => void loadScripts(job.id)}
          />
        </TabsContent>

        {/* Submissions Tab */}
        <TabsContent value="submissions">
          <Card>
            <CardContent className="p-6">
              <p className="text-glass-text-3 text-center py-8">
                Content submissions will appear here
              </p>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="workflow">
          <AIWorkflowPanel mode="job" jobId={job.id} />
        </TabsContent>

        <TabsContent value="insights">
          <AIInsightsPanel mode="job" jobId={job.id} />
        </TabsContent>

        <TabsContent value="budget">
          <Card>
            <CardContent className="p-6 space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-glass-text">Campaign budget</h3>
                <p className="text-sm text-glass-text-2 mt-1">
                  Job total budget field: RM {job.totalBudgetRM ?? '—'}. Track allocated vs spent below.
                </p>
              </div>
              {campaignBudget && (
                <div className="grid sm:grid-cols-3 gap-4">
                  <div>
                    <label className="text-xs text-glass-text-3 block mb-1">Allocated (RM)</label>
                    <Input
                      type="number"
                      value={budgetForm.allocatedRM}
                      onChange={(e) => setBudgetForm({ ...budgetForm, allocatedRM: parseInt(e.target.value, 10) || 0 })}
                    />
                  </div>
                  <div>
                    <label className="text-xs text-glass-text-3 block mb-1">Spent (RM)</label>
                    <Input
                      type="number"
                      value={budgetForm.spentRM}
                      onChange={(e) => setBudgetForm({ ...budgetForm, spentRM: parseInt(e.target.value, 10) || 0 })}
                    />
                  </div>
                  <div>
                    <label className="text-xs text-glass-text-3 block mb-1">Alert at usage %</label>
                    <Input
                      type="number"
                      value={budgetForm.alertAtPct}
                      onChange={(e) => setBudgetForm({ ...budgetForm, alertAtPct: parseInt(e.target.value, 10) || 90 })}
                    />
                  </div>
                </div>
              )}
              {campaignBudget && budgetForm.allocatedRM > 0 && (
                <div
                  className={`rounded-xl p-3 text-sm border ${
                    budgetForm.spentRM >= (budgetForm.allocatedRM * budgetForm.alertAtPct) / 100
                      ? 'bg-amber-400/10 border-amber-400/30 text-amber-500 dark:text-amber-300'
                      : 'bg-emerald-500/10 border-emerald-500/30 text-emerald-500 dark:text-emerald-300'
                  }`}
                >
                  {budgetForm.spentRM >= (budgetForm.allocatedRM * budgetForm.alertAtPct) / 100
                    ? `Approaching limit: spent RM${budgetForm.spentRM} of RM${budgetForm.allocatedRM} (${Math.round((budgetForm.spentRM / budgetForm.allocatedRM) * 100)}%).`
                    : `Usage: ${Math.round((budgetForm.spentRM / budgetForm.allocatedRM) * 100)}% of allocated budget.`}
                </div>
              )}
              <Button onClick={saveBudget} disabled={savingBudget || !campaignBudget}>
                {savingBudget ? 'Saving...' : 'Save budget'}
              </Button>

              <AIPricingPanel
                jobId={job.id}
                totalBudgetRM={job.totalBudgetRM}
                kols={invitations
                  .filter((inv) => inv.kolProfileId && inv.kolProfile?.name)
                  .map((inv) => ({ id: inv.kolProfileId, name: inv.kolProfile!.name }))}
                className="border-0 bg-transparent shadow-none"
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="discovery">
          <AIKOLDiscoveryPanel
            mode="job"
            jobId={job.id}
            kols={invitations
              .filter((inv) => inv.kolProfileId && inv.kolProfile?.name)
              .map((inv) => ({ id: inv.kolProfileId, name: inv.kolProfile!.name }))}
          />
        </TabsContent>

        <TabsContent value="chat">
          <Card>
            <CardContent className="p-6">
              <h3 className="font-semibold text-glass-text mb-2">Campaign room</h3>
              <p className="text-sm text-glass-text-2 mb-4">Real-time messages for agency and brand users on this job.</p>
              {job && <CampaignChat jobId={job.id} />}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="operations">
          <Card>
            <CardContent className="p-6">
              {job && <JobOperationsTab jobId={job.id} />}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="notification">
          <Card>
            <CardContent className="p-6 space-y-4">
              <div>
                <h3 className="text-lg font-semibold text-glass-text">Campaign notifications</h3>
                <p className="text-sm text-glass-text-2 mt-1">
                  Choose which channels and templates apply to this campaign. In-app and email are
                  available now. WhatsApp and SMS are coming soon (placeholders stay off when saved).
                </p>
              </div>
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  className="mt-1 rounded border-glass accent-accent2"
                  checked={messagingEnabled}
                  disabled={savingMessaging}
                  onChange={(e) => setMessagingEnabled(e.target.checked)}
                />
                <span>
                  <span className="font-semibold text-glass-text">Enable automated notifications</span>
                  <span className="block text-sm text-glass-text-2 mt-1">
                    When off, no notifications are sent for this campaign (all channels).
                  </span>
                </span>
              </label>
              <div className="overflow-x-auto border border-glass rounded-xl">
                <table className="w-full text-xs sm:text-sm min-w-[56rem]">
                  <thead>
                    <tr className="border-b border-glass bg-glass-2 text-glass-text-2 text-left">
                      <th className="p-2 font-medium">Step / event</th>
                      <th className="p-2 font-medium text-center">In-app</th>
                      <th className="p-2 font-medium text-center">WhatsApp</th>
                      <th className="p-2 font-medium text-center min-w-[7rem]">WA template</th>
                      <th className="p-2 font-medium text-center">Email</th>
                      <th className="p-2 font-medium text-center min-w-[7rem]">Email template</th>
                      <th className="p-2 font-medium text-center">SMS</th>
                      <th className="p-2 font-medium text-center min-w-[7rem]">SMS template</th>
                    </tr>
                  </thead>
                  <tbody>
                    {MESSAGING_EVENTS.map((ev) => {
                      const f = messagingEventFlags[ev.key] || defaultMsgCh();
                      const setCh = (key: keyof MsgCh, v: boolean) => {
                        setMessagingEventFlags((prev) => ({
                          ...prev,
                          [ev.key]: { ...(prev[ev.key] || defaultMsgCh()), [key]: v },
                        }));
                      };
                      const variantsFor = (ch: ExtCh) =>
                        messagingVariants.filter((v) => v.eventKey === ev.key && v.channel === ch);
                      const setTpl = (ch: ExtCh, variantId: string) => {
                        setMessagingTemplateRefs((prev) => {
                          const next = { ...prev, [ev.key]: { ...(prev[ev.key] || {}) } };
                          if (!variantId) delete next[ev.key]![ch];
                          else next[ev.key]![ch] = variantId;
                          if (Object.keys(next[ev.key] || {}).length === 0) delete next[ev.key];
                          return next;
                        });
                      };
                      return (
                        <tr key={ev.key} className="border-b border-glass/60">
                          <td className="p-2 text-glass-text">{ev.label}</td>
                          <td className="p-2 text-center">
                            <input
                              type="checkbox"
                              className="rounded border-glass accent-accent2"
                              checked={f.inApp}
                              disabled={savingMessaging || !messagingEnabled}
                              onChange={(e) => setCh('inApp', e.target.checked)}
                            />
                          </td>
                          <td className="p-2 text-center">
                            <input
                              type="checkbox"
                              className="rounded border-glass accent-accent2 opacity-50"
                              checked={false}
                              disabled
                              title="Coming soon"
                            />
                            <span className="sr-only">WhatsApp coming soon</span>
                          </td>
                          <td className="p-2">
                            <Select
                              className="text-xs opacity-60"
                              value=""
                              disabled
                              title="WhatsApp templates — coming soon"
                            >
                              <option value="">Coming soon</option>
                            </Select>
                          </td>
                          <td className="p-2 text-center">
                            <input
                              type="checkbox"
                              className="rounded border-glass accent-accent2"
                              checked={f.email}
                              disabled={savingMessaging || !messagingEnabled}
                              onChange={(e) => setCh('email', e.target.checked)}
                            />
                          </td>
                          <td className="p-2">
                            <Select
                              className="text-xs"
                              value={messagingTemplateRefs[ev.key]?.EMAIL || ''}
                              disabled={savingMessaging || !messagingEnabled || !job.managedByOrgId}
                              onChange={(e) => {
                                const v = e.target.value;
                                if (v === '__add__') {
                                  navigate(
                                    `/agency/messaging-templates?event=${encodeURIComponent(ev.key)}&channel=EMAIL`
                                  );
                                  return;
                                }
                                setTpl('EMAIL', v);
                              }}
                            >
                              <option value="">Default</option>
                              {variantsFor('EMAIL').map((v) => (
                                <option key={v.id} value={v.id}>
                                  {v.name}
                                </option>
                              ))}
                              <option value="__add__">+ Add template…</option>
                            </Select>
                          </td>
                          <td className="p-2 text-center">
                            <input
                              type="checkbox"
                              className="rounded border-glass accent-accent2 opacity-50"
                              checked={false}
                              disabled
                              title="Coming soon"
                            />
                          </td>
                          <td className="p-2">
                            <Select
                              className="text-xs opacity-60"
                              value=""
                              disabled
                              title="SMS templates — coming soon"
                            >
                              <option value="">Coming soon</option>
                            </Select>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <Button type="button" size="sm" onClick={saveMessagingSettings} disabled={savingMessaging}>
                {savingMessaging ? 'Saving…' : 'Save notification settings'}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="contracts">
          <Card>
            <CardContent className="p-6">
              {job?.managedByOrgId ? (
                <JobContractsTab jobId={job.id} agencyOrgId={job.managedByOrgId} />
              ) : (
                <p className="text-sm text-glass-text-3">Contracts need a managing agency on this job.</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {mobilePrimaryAction && (
        <div className="fixed inset-x-0 bottom-[calc(56px+env(safe-area-inset-bottom))] z-30 flex gap-2 border-t border-glass bg-glass-3 px-4 py-2 backdrop-blur-[28px] md:hidden">
          <label
            className={`relative flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border transition-colors focus-within:ring-2 focus-within:ring-accent2/60 ${
              isCampaignToolTab
                ? 'border-accent2/30 bg-accent2/15 text-accentText'
                : 'border-glass bg-glass-4 text-glass-text-2'
            }`}
            title="More campaign tools"
          >
            <Settings className="h-[18px] w-[18px]" aria-hidden />
            <select
              value={isCampaignToolTab ? activeMainTab : ''}
              onChange={(e) => {
                const v = e.target.value;
                if (v) setActiveMainTab(v);
              }}
              className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
              aria-label="More campaign tools"
            >
              <option value="">More tools</option>
              {CAMPAIGN_TOOL_TABS.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            onClick={mobilePrimaryAction.onClick}
            disabled={mobilePrimaryAction.disabled}
            className="btn-accent inline-flex h-11 min-w-0 flex-1 items-center justify-center gap-2 px-4 text-sm font-semibold disabled:cursor-not-allowed disabled:opacity-60"
          >
            <Sparkles className="h-[17px] w-[17px] shrink-0" aria-hidden />
            <span className="truncate">{mobilePrimaryAction.label}</span>
          </button>
        </div>
      )}

      {/* Manual KOL Selector Modal */}
      {showManualSelector && job && (
        <ManualKOLSelector
          agencyOrgId={job.managedByOrgId || ''}
          organizationId={job.managedByOrgId || undefined}
          onClose={() => setShowManualSelector(false)}
          onBroadcast={handleManualBroadcast}
        />
      )}

      {/* Broadcast confirmation — shows the actual invitation email before anything is sent */}
      {broadcastConfirm && (
        <BroadcastConfirmModal
          isOpen
          count={broadcastPreview?.count ?? broadcastConfirm.kolIds.length}
          kolNames={broadcastPreview?.kolNames ?? []}
          email={broadcastPreview?.email ?? null}
          loading={broadcastPreviewLoading}
          previewUnavailableReason={broadcastPreviewError}
          sending={broadcasting}
          onCancel={closeBroadcastConfirm}
          onConfirm={() => void confirmBroadcast()}
        />
      )}

      {/* Edit Match Criteria Modal */}
      {showEditCriteria && job && (
        <EditMatchCriteriaModal
          jobId={job.id}
          currentCriteria={{
            tags: job.jobTags || [],
            demographicMix: job.demographicMix,
            estimatedKOL: job.estimatedKOL || 10,
            broadcastMultiplier: job.broadcastMultiplier ?? 3,
            shortlistMultiplier: job.shortlistMultiplier ?? 1,
          }}
          onClose={() => setShowEditCriteria(false)}
          onSave={() => {
            setShowEditCriteria(false);
            loadJob();
          }}
        />
      )}
    </div>
  );
}
